import React from "react";
import { ShieldCheck, Wind, FileCheck2, Link2 } from "lucide-react";
import { PageHero, Reveal, Label, CTASection } from "../components/Shared";
import Ribbon from "../components/Ribbon";
import { COMPLIANCE, IMAGES } from "../data/site";

const ICONS = [ShieldCheck, Wind, FileCheck2, Link2];

const DOCS = [
  "Certificate of Analysis (CoA)",
  "Safety Data Sheets (SDS)",
  "ISO Cleanroom Certification",
  "Chain of Custody Records",
];

export default function Quality() {
  return (
    <>
      <PageHero
        label="Quality & Compliance"
        titleLines={["Everything above", "board. Everything", "documented."]}
        intro="Our credentials are the core of who we are. FDA 510(k) clearance and ISO 7 cleanroom certification are backed by complete, verifiable documentation."
        image={IMAGES.microscope}
        imageAlt="Quality control under the microscope"
      />
      <Ribbon />

      {/* FDA prominence */}
      <section className="bg-navy py-24 lg:py-32">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-12">
          <div className="grid gap-10 lg:grid-cols-12 lg:items-center">
            <Reveal className="lg:col-span-7">
              <Label light>Federal Clearance</Label>
              <h2 className="mt-6 font-heading text-4xl font-extrabold leading-[1.05] tracking-tight text-white sm:text-6xl">
                FDA 510(k) Medical
                <br />
                Device Clearance.
              </h2>
              <p className="mt-8 max-w-xl text-lg leading-relaxed text-slate-300">
                A 510(k) clearance demonstrates that our device is substantially
                equivalent to a legally marketed device — and that it's produced
                under regulated quality standards. It is the foundation of our
                credibility.
              </p>
            </Reveal>
            <Reveal delay={0.15} className="lg:col-span-4 lg:col-start-9">
              <div className="border border-white/15 p-10">
                <ShieldCheck className="h-14 w-14 text-clinical" />
                <div className="mt-8 font-display text-6xl font-extrabold text-white">
                  510(k)
                </div>
                <div className="mt-3 text-sm font-semibold uppercase tracking-[0.2em] text-slate-400">
                  Cleared Medical Device
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ISO breakdown */}
      <section className="bg-white py-24 lg:py-32">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-12">
          <Reveal>
            <Label>ISO 14644-1:2015</Label>
            <h2 className="mt-6 max-w-3xl font-heading text-4xl font-extrabold tracking-tight text-navy sm:text-5xl">
              A cleanroom certified at two classifications.
            </h2>
            <p className="mt-6 max-w-2xl text-lg text-slate-600">
              Certified by Clean Room Services of Canoga Park, CA.
            </p>
          </Reveal>

          <div className="mt-16 grid gap-6 md:grid-cols-2">
            <Reveal>
              <div className="group h-full border border-slate-200 p-10 transition-colors duration-300 hover:border-clinical">
                <span className="font-display text-sm font-bold text-clinical">
                  ANTE ROOM
                </span>
                <div className="mt-6 font-display text-6xl font-extrabold text-navy">
                  ISO 8
                </div>
                <p className="mt-6 text-[15px] leading-relaxed text-slate-500">
                  The transitional environment protecting the core cleanroom —
                  certified to ISO 8 standards for controlled entry and gowning.
                </p>
              </div>
            </Reveal>
            <Reveal delay={0.1}>
              <div className="group h-full border border-slate-200 bg-navy p-10">
                <span className="font-display text-sm font-bold text-clinical">
                  CLEAN ROOM
                </span>
                <div className="mt-6 font-display text-6xl font-extrabold text-white">
                  ISO 7
                </div>
                <p className="mt-6 text-[15px] leading-relaxed text-slate-300">
                  The core manufacturing environment — certified to ISO 7 for
                  strict control of airborne particulate concentration.
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Compliance list */}
      <section className="bg-[#FAFAFA] py-24 lg:py-32">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-12">
          <div className="grid gap-12 lg:grid-cols-12">
            <Reveal className="lg:col-span-4">
              <Label>The Full Picture</Label>
              <h2 className="mt-6 font-heading text-4xl font-extrabold tracking-tight text-navy sm:text-5xl">
                Reassurance, documented.
              </h2>
            </Reveal>
            <div className="lg:col-span-8">
              <div className="divide-y divide-slate-200 border-y border-slate-200">
                {COMPLIANCE.map((c, i) => {
                  const Icon = ICONS[i];
                  return (
                    <Reveal key={c.title} delay={i * 0.06}>
                      <div className="group grid gap-6 py-8 sm:grid-cols-12 sm:items-start">
                        <div className="sm:col-span-1">
                          <Icon className="h-7 w-7 text-clinical" />
                        </div>
                        <div className="sm:col-span-3">
                          <span className="text-xs font-bold uppercase tracking-[0.18em] text-slate-400">
                            {c.tag}
                          </span>
                          <h3 className="mt-2 font-heading text-lg font-bold text-navy">
                            {c.title}
                          </h3>
                        </div>
                        <p className="text-[15px] leading-relaxed text-slate-500 sm:col-span-8">
                          {c.body}
                        </p>
                      </div>
                    </Reveal>
                  );
                })}
              </div>

              <Reveal delay={0.2}>
                <div className="mt-12 border border-slate-200 bg-white p-8 lg:p-10">
                  <span className="text-xs font-bold uppercase tracking-[0.18em] text-clinical">
                    Documentation Package
                  </span>
                  <div className="mt-6 grid gap-4 sm:grid-cols-2">
                    {DOCS.map((d) => (
                      <div key={d} className="flex items-center gap-3">
                        <FileCheck2 className="h-5 w-5 shrink-0 text-clinical" />
                        <span className="text-[15px] font-medium text-navy">
                          {d}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      <CTASection />
    </>
  );
}
